import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Input,
  Select,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  Tabs,
  DatePicker,
  KpiCard,
} from '@bbpos/ui';
import type { ColumnDef, DateRange } from '@bbpos/ui';
import {
  useCommLogs,
  useCommLogsSummary,
  ChannelPill,
  StatusBadge,
} from '@/features/communications';
import type { CommunicationLog, CommChannel, CommLogStatus, CommLogType } from '@/features/communications';

const LOG_TYPE_TABS = [
  { id: '', label: 'All' },
  { id: 'INVOICE', label: 'Invoices' },
  { id: 'PROMO', label: 'Promotions' },
  { id: 'SOCIAL_POST', label: 'Social' },
  { id: 'DEAL_OF_DAY', label: 'Deals' },
  { id: 'REORDER_LINK', label: 'Re-order Links' },
  { id: 'FEEDBACK_REQUEST', label: 'Feedback' },
  { id: 'PURCHASE_ORDER', label: 'Purchase Orders' },
  { id: 'RFQ', label: 'RFQs' },
];

const CHANNEL_OPTIONS = [
  { value: '', label: 'All Channels' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'FACEBOOK', label: 'Facebook' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'SENT', label: 'Sent' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'READ', label: 'Read' },
  { value: 'FAILED', label: 'Failed' },
];

// A link share has no recipient; rows logged before CU4-007 hold the quote's
// approval URL (a bearer link) — never show it.
function maskRecipient(recipient: string): string {
  if (recipient === 'BROADCAST') return 'Broadcast';
  if (recipient === 'LINK' || /^https?:\/\//.test(recipient)) return 'Public link';
  if (recipient.includes('@')) {
    const [local, domain] = recipient.split('@');
    return `${local[0]}***@${domain}`;
  }
  if (recipient.startsWith('+') && recipient.length > 6) {
    return `${recipient.slice(0, 4)}****${recipient.slice(-4)}`;
  }
  return recipient;
}

type Row = CommunicationLog;

// Quotation sends are stored as type INVOICE (chk_comm_logs_type has no
// QUOTATION) — label them by their reference instead.
function logTypeLabel(row: Row): string {
  return row.reference_type === 'QUOTATION' ? 'Quotation' : row.type.replace(/_/g, ' ');
}

function LogDetails({ row }: { row: Row }) {
  if (row.failure_code || row.failure_reason) {
    return (
      <span className="text-xs text-danger-600 dark:text-danger-400">
        {row.failure_code && <span className="font-mono">[{row.failure_code}] </span>}
        {row.failure_reason}
      </span>
    );
  }
  const stamp = row.read_at ?? row.delivered_at;
  return (
    <span className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
      {stamp && `${row.read_at ? 'Read' : 'Delivered'} ${new Date(stamp).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`}
      {row.provider_message_id && <span className="block truncate max-w-[16rem]">Ref: {row.provider_message_id}</span>}
    </span>
  );
}

const columns: ColumnDef<Row>[] = [
  {
    key: 'type',
    header: 'Type',
    render: (_v, row) => (
      <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
        {logTypeLabel(row)}
      </span>
    ),
  },
  {
    key: 'channel',
    header: 'Channel',
    render: (_v, row) => <ChannelPill channel={row.channel} />,
  },
  {
    key: 'recipient',
    header: 'Recipient',
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-200 font-mono tabular-nums">
        {maskRecipient(row.recipient)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => <StatusBadge status={row.status} variant="log" />,
  },
  {
    key: 'failure_reason',
    header: 'Details',
    render: (_v, row) => <LogDetails row={row} />,
  },
  {
    key: 'created_at',
    header: 'Date',
    render: (_v, row) => (
      <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
        {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        {' '}
        {new Date(row.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
      </span>
    ),
  },
];

function CommunicationLogsContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement | null>(null);

  const page = Number(searchParams.get('page') ?? '1');
  const channel = (searchParams.get('channel') ?? '') as CommChannel | '';
  const status = (searchParams.get('status') ?? '') as CommLogStatus | '';
  const type = (searchParams.get('type') ?? '') as CommLogType | '';
  const search = searchParams.get('search') ?? '';
  const startDate = searchParams.get('start_date') ?? '';
  const endDate = searchParams.get('end_date') ?? '';

  const { data, isLoading, isFetching } = useCommLogs({
    page,
    channel,
    status,
    type,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    search: search || undefined,
  });
  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);
  const { data: summary, isLoading: summaryLoading } = useCommLogsSummary();

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  // Keyboard: / → focus search
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  function handleDateRangeChange(range: DateRange) {
    const params = new URLSearchParams(searchParams);
    params.set('start_date', range.from.toISOString().split('T')[0]);
    params.set('end_date', range.to.toISOString().split('T')[0]);
    params.set('page', '1');
    setSearchParams(params);
  }

  const dateRangeValue = useMemo(() => {
    if (startDate && endDate) {
      return { from: new Date(startDate), to: new Date(endDate) };
    }
    return undefined;
  }, [startDate, endDate]);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-500 via-cyan-500 to-emerald-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(20,184,166,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            Audit Trail
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Communication Logs</h1>
          <p className="mt-1.5 text-sm text-white/90 max-w-xl">
            Track every outbound message — invoices, promos, deals, and campaigns across all channels.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums font-mono">
              {(data?.meta.total ?? 0).toLocaleString('en-IN')} total messages
            </span>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={90} className="rounded-xl" />
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            title="Total Sent"
            value={summary.total_sent.toLocaleString('en-IN')}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            }
            className="bg-gradient-to-br from-brand-50 to-indigo-50 dark:from-brand-900/20 dark:to-indigo-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
          <KpiCard
            title="Delivery Rate"
            value={`${summary.delivery_rate}%`}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            className="bg-gradient-to-br from-success-50 to-emerald-50 dark:from-success-900/20 dark:to-emerald-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
          <KpiCard
            title="Read Rate"
            value={`${summary.read_rate}%`}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
            className="bg-gradient-to-br from-violet-50 to-pink-50 dark:from-violet-900/20 dark:to-pink-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
          <KpiCard
            title="Re-order Clicks"
            value={summary.reorder_clicks.toLocaleString('en-IN')}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
            }
            className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 shadow-md hover:shadow-lg transition-all duration-200"
          />
        </div>
      ) : null}

      {/* Type Tabs */}
      <div className="rounded-xl bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm shadow-md p-4 sm:p-5 mb-6">
        <Tabs
          items={LOG_TYPE_TABS.map((t) => ({ id: t.id, label: t.label }))}
          activeId={type}
          onChange={(id) => setParam('type', id)}
        />

        {/* Filters */}
        <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end flex-wrap">
          <div className="w-full lg:w-56">
            <Input
              ref={searchRef}
              value={search}
              onChange={(v) => setParam('search', v)}
              placeholder="Search recipient... (/)"
              aria-label="Search by recipient"
            />
          </div>
          <div className="w-full lg:w-40">
            <Select
              label="Channel"
              options={CHANNEL_OPTIONS}
              value={channel}
              onChange={(v) => setParam('channel', typeof v === 'string' ? v : '')}
              placeholder="All Channels"
            />
          </div>
          <div className="w-full lg:w-40">
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={status}
              onChange={(v) => setParam('status', typeof v === 'string' ? v : '')}
              placeholder="All Statuses"
            />
          </div>
          <div className="w-full lg:w-64">
            <DatePicker
              variant="range"
              label="Date Range"
              value={dateRangeValue}
              onChange={handleDateRangeChange}
              placeholder="Select date range"
            />
          </div>
        </div>
      </div>

      {/* Desktop Table / Mobile Cards */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="No messages found"
            description="Adjust your filters or send your first communication."
            icon={
              <svg className="h-20 w-20 text-teal-300 dark:text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
          />
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <section className="hidden sm:block rounded-2xl bg-white dark:bg-neutral-800 shadow-lg overflow-hidden animate-slide-up" aria-label="Communication logs">
            <DataTable<Row>
              columns={columns}
              data={rows}
              loading={isFetching}
              totalCount={data?.meta.total}
              currentPage={page}
              pageSize={50}
              onPageChange={(p) => setParam('page', String(p))}
              stickyHeader
            />
          </section>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-3 animate-slide-up">
            {rows.map((row) => (
              <div key={row.id} className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
                    {logTypeLabel(row)}
                  </span>
                  <StatusBadge status={row.status} variant="log" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <ChannelPill channel={row.channel} />
                  <span className="text-sm text-neutral-700 dark:text-neutral-200 font-mono tabular-nums">
                    {maskRecipient(row.recipient)}
                  </span>
                </div>
                <div className="mb-2">
                  <LogDetails row={row} />
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
                  {new Date(row.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              </div>
            ))}
            {/* Simple pagination for mobile */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setParam('page', String(page - 1))}
              >
                Previous
              </Button>
              <span className="text-xs text-neutral-500 font-mono tabular-nums">
                Page {page} of {data?.meta.totalPages ?? 1}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= (data?.meta.totalPages ?? 1)}
                onClick={() => setParam('page', String(page + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function CommunicationLogsPage() {
  return (
    <PageContainer
      title="Communication Logs"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Communications', href: '/communications' },
        { label: 'Logs' },
      ]}
    >
      <ErrorBoundary resetKey="comm-logs">
        <CommunicationLogsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
